import { z } from "zod";

/**
 * `EQUIPE-GESTURES`, round 2: **the answers of the chef's three gestures, as the doors of round 1 give them** (`GET`/`POST
 * /api/v1/bank/files/{id}/reassign`, `GET`/`POST /api/v1/bank/team/{userId}/portfolio/transfer`, `GET /api/v1/bank/team/journal`),
 * the shape the bank client PARSES at its boundary instead of casting (A48). Loose objects: a key the server adds is not a mismatch,
 * a key the screen reads that is missing is.
 *
 * An analyst set aside travels with a typed `kind` and `returnsOn` , the screen writes the words and the date itself, and never prints
 * the server's `reason` (the audit's own string, with the date in ISO form).
 */
export const bankExcludedSchema = z.looseObject({
  userId: z.string(),
  fullName: z.string(),
  kind: z.enum(["invitationEnAttente", "absent", "desactive"]),
  returnsOn: z.string().nullable(),
  reason: z.string(),
});

export const reassignmentPreviewSchema = z.looseObject({
  file: z.looseObject({ id: z.string(), reference: z.string(), stage: z.string(), weight: z.number() }),
  carrier: z.looseObject({ userId: z.string(), fullName: z.string() }),
  proposal: z.looseObject({
    assigneeUserId: z.string().nullable(),
    assigneeFullName: z.string().nullable(),
    rule: z.string(),
    motif: z.string(),
    runId: z.string().nullable(),
  }),
  excluded: z.array(bankExcludedSchema),
  targets: z.array(
    z.looseObject({
      userId: z.string(),
      fullName: z.string(),
      points: z.number(),
      resultingPoints: z.number(),
    }),
  ),
});

export const fileMovedSchema = z.looseObject({
  fileId: z.string(),
  reference: z.string(),
  fromUserId: z.string(),
  toUserId: z.string(),
  toFullName: z.string(),
  rule: z.string(),
  origin: z.string(),
  motif: z.string(),
});

export const transferPreviewSchema = z.looseObject({
  from: z.looseObject({ userId: z.string(), fullName: z.string() }),
  lines: z.array(
    z.looseObject({
      fileId: z.string(),
      reference: z.string(),
      clientName: z.string().nullable(),
      stage: z.string(),
      weight: z.number(),
      targetUserId: z.string().nullable(),
      targetFullName: z.string().nullable(),
      rule: z.string(),
      motif: z.string(),
    }),
  ),
  excluded: z.array(bankExcludedSchema),
  impossible: z.boolean(),
});

export const transferDoneSchema = z.looseObject({
  transferred: z.number().int(),
  moves: z.array(fileMovedSchema),
});

export const teamJournalSchema = z.looseObject({
  lines: z.array(
    z.looseObject({
      at: z.string(),
      fileId: z.string(),
      reference: z.string(),
      clientName: z.string().nullable(),
      fromName: z.string().nullable(),
      toName: z.string().nullable(),
      byName: z.string().nullable(),
      origin: z.string().nullable(),
      rule: z.string().nullable(),
      motif: z.string(),
    }),
  ),
});

export type BankExcluded = z.infer<typeof bankExcludedSchema>;
export type ReassignmentPreview = z.infer<typeof reassignmentPreviewSchema>;
export type FileMoved = z.infer<typeof fileMovedSchema>;
export type TransferPreview = z.infer<typeof transferPreviewSchema>;
export type TransferDone = z.infer<typeof transferDoneSchema>;
export type TeamJournal = z.infer<typeof teamJournalSchema>;
